<script lang="ts">
  import { onMount } from 'svelte'
  import videoStore from '../stores/video.svelte'
  import componentAttributesStore from '../stores/componentAttributes.svelte'
  const { manifest }: { manifest: string } = $props()
  let scriptLoaded = false
  let playerController: cast.framework.RemotePlayerController | null = null
  let player: cast.framework.RemotePlayer | null = null
  let castSession: cast.framework.CastSession | null = null

  onMount(() => {
    if (
      scriptLoaded ||
      document.querySelector('script[src*="cast_framework.js"]')
    ) {
      scriptLoaded = true
      return
    }

    scriptLoaded = true

    window.__onGCastApiAvailable = function (isAvailable) {
      if (isAvailable) {
        onScriptsLoaded()
        videoStore.state.isChromecastSupported = true
      }
    }

    const scriptFramework = document.createElement('script')
    scriptFramework.async = true
    scriptFramework.src =
      'https://www.gstatic.com/cast/sdk/libs/sender/1.0/cast_framework.js'

    scriptFramework.onload = () => {
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js'
      document.body.appendChild(script)
    }
    document.body.appendChild(scriptFramework)

    return () => {
      castSession?.endSession(true)
    }
  })

  async function onSessionStart() {
    if (!castSession) return

    if (await loadMedia()) {
      initPlayerAndController()
    }
  }

  function stopCastingOnError(error: unknown) {
    console.error('Chromecast: media could not be played', error)
    if (player) {
      videoStore.state.videoController.setCurrentTime(player.currentTime)
    }
    // stopCasting = true also stops the receiver app; SESSION_ENDED then resets the local state
    castSession?.endSession(true)
  }

  function initSession(context: cast.framework.CastContext) {
    videoStore.state.isChromecasting = true
    castSession = context.getCurrentSession()
    videoStore.state.chromecastDeviceName =
      castSession?.getCastDevice().friendlyName ?? null
  }

  function initMediaListener() {
    const media = castSession && castSession.getMediaSession()

    if (media) {
      // We use this listener to use idleReason value because in we use the player listener PLAYER_STATE_CHANGED, the media (so the idleReason) could be null when the platerState is IDLE
      media.addUpdateListener((isAlive) => {
        if (media.playerState === chrome.cast.media.PlayerState.IDLE) {
          if (media.idleReason === chrome.cast.media.IdleReason.FINISHED) {
            videoStore.state.currentTime = videoStore.state.duration
            videoStore.state.isEnded = true
            videoStore.state.isPlaying = false
          }
          // The receiver may accept the load request and fail afterwards (e.g. unreachable manifest/segments)
          if (media.idleReason === chrome.cast.media.IdleReason.ERROR) {
            stopCastingOnError(media.idleReason)
            return
          }
        }
      })
    }
  }

  function initPlayerAndController() {
    player = new cast.framework.RemotePlayer()

    player.currentTime = videoStore.state.currentTime

    playerController = new cast.framework.RemotePlayerController(player)

    if (player?.volumeLevel !== componentAttributesStore.state.volume) {
      setVolume(componentAttributesStore.state.volume)
    }

    if (
      castSession?.getMediaSession()?.playerState ===
      chrome.cast.media.PlayerState.PLAYING
    ) {
      videoStore.state.isPlaying = true
    }

    playerController.addEventListener(
      cast.framework.RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED,
      ({ value: isMedia }) => {
        if (isMedia) {
          if (player) {
            player.currentTime = videoStore.state.currentTime
          }
          playerController?.seek()
          videoStore.state.isPlaying = true

          initMediaListener()
        }
      },
    )

    playerController.addEventListener(
      cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
      ({ value: currentTime }) => {
        if (currentTime && currentTime !== 0) {
          videoStore.state.currentTime = currentTime
        }
      },
    )

    playerController.addEventListener(
      cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
      (event) => {
        if (
          event.value === chrome.cast.media.PlayerState.BUFFERING &&
          !videoStore.state.isBuffering
        ) {
          videoStore.state.isBuffering = true
        }

        if (
          event.value === chrome.cast.media.PlayerState.PLAYING &&
          videoStore.state.isBuffering
        ) {
          videoStore.state.isBuffering = false
        }
      },
    )
  }

  export async function loadMedia(withReset = false) {
    if (withReset) {
      videoStore.state.currentTime = 0
      videoStore.state.isEnded = false
    }
    videoStore.state.isBuffering = true
    videoStore.state.isPlaying = true
    if (!castSession) return false
    try {
      await castSession.loadMedia(
        new chrome.cast.media.LoadRequest(
          new chrome.cast.media.MediaInfo(manifest, 'application/x-mpegURL'),
        ),
      )
      return true
    } catch (errorCode) {
      // chrome.cast.ErrorCode, e.g. 'load_media_failed', 'session_error', 'timeout'
      stopCastingOnError(errorCode)
      return false
    }
  }

  export function seek(time: number) {
    if (player && playerController) {
      player.currentTime = player.currentTime + time
      playerController?.seek()
    }
  }

  export function setCurrentTime(time: number) {
    if (player) {
      player.currentTime = time
      playerController?.seek()
    }
  }

  export function toggleCast() {
    if (castSession) {
      castSession.endSession(true)
    } else {
      cast.framework.CastContext.getInstance().requestSession()
    }
  }

  export function togglePlayPause() {
    if (playerController) {
      playerController.playOrPause()
      videoStore.state.isPlaying = !videoStore.state.isPlaying
    }
  }

  export function setVolume(level: number) {
    // level: 0.0 → 1.0
    if (player && player.canControlVolume && playerController) {
      player.volumeLevel = level // set the value first...
      playerController.setVolumeLevel() // ...then apply it
    }
  }

  export function toggleMute() {
    if (playerController) {
      playerController.muteOrUnmute()
    }
  }

  function onScriptsLoaded() {
    if (!window.chrome || !window.chrome.cast || !window.cast) {
      console.warn('Chrome Cast API not available yet')
      return
    }

    const applicationId =
      chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID || 'CC1AD845'

    const context = cast.framework.CastContext.getInstance()
    context.setOptions({
      receiverApplicationId: applicationId,
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
    })

    context.addEventListener(
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      (event) => {
        switch (event.sessionState) {
          case cast.framework.SessionState.SESSION_STARTED:
            initSession(context)
            onSessionStart()
            break
          case cast.framework.SessionState.SESSION_RESUMED:
            videoStore.state.hasPlaybackStarted = true
            initSession(context)
            initPlayerAndController()
            initMediaListener()
            // This is a hack to resync the right player currentTime ( a delta appears randomly between the real currenttime and the one get from player)
            playerController?.playOrPause()
            playerController?.playOrPause()
            break
          case cast.framework.SessionState.SESSION_ENDED:
            videoStore.state.isBuffering = false
            videoStore.state.isPlaying = false
            videoStore.state.isChromecasting = false
            if (videoStore.state.isEnded) {
              videoStore.state.videoController.setCurrentTime(
                videoStore.state.duration - 0.01,
              )
              setTimeout(() => {
                videoStore.state.isEnded = true
              }, 100)
              videoStore.state.isEnded = true
            } else if (player && player.currentTime !== 0) {
              videoStore.state.videoController.setCurrentTime(
                player.currentTime,
              )
            }
            castSession = null
            break
        }
      },
    )
  }
</script>
